// ThreadsPage.jsx — 第一百零四批 B：组间通信（/threads）。
//
// ── 它读的是什么 ────────────────────────────────────────────────────────────
//   GET  /api/group-threads          → 线列表（主题、参与组、状态、消息数、最新时间）
//   GET  /api/group-threads/<id>     → 线 + 消息流（seq 升序）
//   POST /api/group-threads          → 发起新线（canLiaison 限定，后端 403 把守）
//   POST /api/group-threads/<id>/messages → 回帖（同上）
//   POST /api/group-threads/<id>/parties  → 追加参与组（第一百零六批 C1）
//   POST /api/group-threads/<id>/archive        → 提议归档（第一百二十五批 C）
//   POST /api/group-threads/<id>/archive/cancel → 取消归档（同上）
//   POST /api/group-threads/<id>/reopen         → 重开已归档的线（同上）
//
// ── 第一百二十五批 C：归档 + 三日否决窗口（需求方 2026-08-20 三裁）─────────────
// 「①任意参与方可发起归档；②三日内任意参与方可取消，到期无异议则自动归档；
//  ③归档＝只读 + 默认列表隐藏 + 可重开；④窗口内任一方发新言即自动取消。」
// 落在本页的三处：**本页的第一个筛选器**（默认隐藏已归档，可切换；过滤在服务端
// 做，见 ThreadsBoard 里那段注释）、详情头下的三按钮动作条（同一时刻只出现一个）、
// 以及"待归档"那枚带到期时刻的徽标。归档态**不重算**：读服务端的 archiveState。
// 全部显隐判据住在 threads.mjs（`.jsx` 不进单测，SB-042）。
//
// ── 第一百零六批 C1：两端 → 参与组集合 ─────────────────────────────────────
// 线不再是双边。徽标从"两端"变成**参与组列表**（N 枚）；发起对话框的目标组改成
// 多选；回帖代表组的选项＝参与集 ∩ 我的组（口径未变，只换了来源）；多出一个
// 「拉一个组进来」的入口（canLiaison ∧ 我已在场）。读面依旧全员透明，一个字没改。
//
// ── 透明口径（需求方裁）───────────────────────────────────────────────────────
// 本页**不按当前组过滤**：执行层成员全量可读。这与 Reports/WorkItems 的"按
// currentGroup 分片"是刻意相反的两种姿态——组间通信的意义就是让每个成员看得见组与
// 组之间在说什么，按组切片恰好毁掉这一点。所以这页不读 currentGroup 来取数（只借它
// 给新线表单填默认发起组），也没有 noGroup 空态；页头一句话把这个口径说给用户听
//（threads.subtitle），否则"看得见别组的线"读起来像一个漏洞。
//
// ── 第一百零八批 C1：谁算"全员"（需求方裁）───────────────────────────────────
// 透明口径的**理由**是"让公司成员看得见组与组之间在说什么"，于是两类人落在范围外：
// 合同相对方（party=external：外包、外部顾问，不是公司成员），以及资方
//（party=principal：**是**公司成员，但这页是执行层的工作面而不是向所有者的汇报面——
// 向资方的交代走汇报面与审批面）。判据是 `canSeeThreadsPage`（＝appNav.mjs 的
// `isInternalParty`），与后端 `is_internal_party` 共用 /api/me 的同一枚 `party`，
// **不是**这里另写的一张名单。
// 本页对他们**先于任何取数**渲染一句拒绝：一个空页与"系统坏了"无法区分，而后端
// 对他们整族 404，不拦就是打一串注定 404 的请求再显示"加载失败"。
// 真边界仍在后端（这里藏起来的东西，直接打接口同样拿不到）。
//
// ── 写面显隐 ────────────────────────────────────────────────────────────────
// 依据是 /api/me account 块的 canLiaison：false ⇒ 零写入控件（没有发起按钮、没有
// 回帖框——不是禁用，是**不存在**：一个永远点不动的控件只会让人以为页面坏了）。
// 显隐只是礼貌，真边界在后端（无权 403），所以这里不做第二遍鉴权逻辑。
//
// ── 正文用同一只渲染器 ──────────────────────────────────────────────────────
// 消息正文走 <MarkdownBody>——工单/工作项/汇报正文那一只，内部是发布管线的
// md-render。理由同 ReportsPage 头注：另写一只渲染器意味着同一段 markdown 在
// 不同页面可以长得不一样，而那种差异没有任何判据看得住。
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItemButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import { useAuth } from '../auth/AuthContext.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useLang, useT } from '../i18n/index.mjs';
import AgentChip from '../components/AgentChip.jsx';
import { canSeeThreadsPage } from '../components/appNav.mjs';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import HelpLink from '../components/HelpLink.jsx';
import MarkdownBody from '../components/MarkdownBody.jsx';
import { fmtTime } from '../components/TicketDetailView.jsx';
import {
  archiveDoneKey,
  canCancelArchive, canInviteParty, canReopenThread, canReplyToThread, canRequestArchive,
  defaultAsGroup, focusIdFromSearch, fromGroupOptions, groupOptionsForNewThread, inviteOptions,
  newThreadParties, newThreadProblem, normalizeThreadDetail, normalizeThreads, partyGroups,
  pickThreadId, replyCancelledArchive, showArchivedFromSearch, threadGroupLabel,
} from './threads.mjs';

// 线状态徽标。open 是常态，不渲染（每行都挂一枚"进行中"只是噪音）；
// 其余状态照 workItems.status 的写法：认得的用 i18n，认不得的如实印原值。
function statusChip(t, status) {
  if (!status || status === 'open') return null;
  const k = `threads.status.${status}`;
  return (
    <Chip
      size="small"
      variant="outlined"
      color="default"
      label={t(k) === k ? status : t(k)}
    />
  );
}

// ── 归档态徽标（第一百二十五批 C）──────────────────────────────────────────────
// 与 statusChip 并排而**不是**合成一个：归档态与 status 是正交的两维（一条 closed
// 的线照样可以归档），合成一枚徽标就要为每一种组合造一个词。
// 「待归档」这一枚是本页最载重的一处文字：读到它的人正在决定要不要出声，而这是
// 整个系统里唯一一处"不作声就算同意"。所以它不只报状态，还把**到期时刻**说出来；
// 窗口被关掉时（后端给 archiveDueAtMs=null ⇒ 这里是 0）改说"不会自动归档"——
// 用一句含糊的"待归档"盖住这个差别，会让人以为自己还有三天。
function archiveChip(t, thread, lang) {
  const s = thread?.archiveState;
  if (!s || s === 'open') return null;
  const pending = s === 'archive_pending';
  const due = thread.archiveDueAtMs;
  const detail = pending
    ? (due ? t('threads.archive.dueAt', { at: fmtTime(due, lang) }) : t('threads.archive.dueNever'))
    : (thread.archiveAuto ? t('threads.archive.autoMark') : '');
  return (
    <Chip
      size="small"
      variant={pending ? 'filled' : 'outlined'}
      color={pending ? 'warning' : 'default'}
      label={detail ? `${t(`threads.archive.state.${s}`)} · ${detail}`
                    : t(`threads.archive.state.${s}`)}
    />
  );
}

// 参与组徽标列表（列表行与详情头共用）。取名走 threadGroupLabel：线上只有 slug，
// 双语名从会话上下文按 slug 找回（threads.mjs 头注）。⇄ 用文字字形——本项目
// 不装 @mui/icons-material。
// N 枚全渲染、**不折叠**："还有 2 个"这种收纳会让"这条线上有谁"变成要点一下才
// 知道的事，而那正是这一页存在的理由。
// partiesInferred（第一百零六批 R2）：这一排徽标不是服务端说的，是按 aGroup/bGroup
// 两端**推得**的。静默回落会让"参与集回填漏了这条线"永远无人发现——那条线看起来
// 与正常的两方线一模一样，直到某个第三方发现自己回不了帖。所以推得的行多挂一枚
// 警示徽标：它是这条线上唯一说得出"这是猜的"的东西。
function PartyChips({ thread, knownGroups, lang }) {
  const t = useT();
  const parties = partyGroups(thread);
  if (parties.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      {parties.map((g, i) => (
        <Stack key={g.slug} direction="row" spacing={0.5} useFlexGap sx={{ alignItems: 'center' }}>
          {i > 0 ? (
            <Box component="span" aria-hidden sx={{ color: 'text.secondary', fontSize: 12 }}>⇄</Box>
          ) : null}
          <Chip size="small" variant="outlined" color="info"
                label={threadGroupLabel(g, knownGroups, lang)} />
        </Stack>
      ))}
      {thread?.partiesInferred ? (
        <Chip size="small" variant="outlined" color="warning"
              label={t('threads.partiesInferred')}
              title={t('threads.partiesInferredHint')} />
      ) : null}
    </Stack>
  );
}

// ── 拉一个组进这条线（canLiaison ∧ 我已在场才渲染）─────────────────────────
// 摆成"选一个组 + 一个按钮"而不是一个对话框：这是一个单字段动作，为它开一扇
// 模态窗只是多两次点击。可选项＝候选全集 − 已在场（inviteOptions）；没有可加的
// 组时整件不渲染——一个永远空的选择器只会让人以为页面坏了。
function InviteParty({ thread, options, knownGroups, lang, onAdded, onError }) {
  const t = useT();
  const [slug, setSlug] = useState('');
  const [busy, setBusy] = useState(false);
  const list = inviteOptions(thread, options);
  if (list.length === 0) return null;
  const pick = list.some((g) => g.slug === slug) ? slug : '';

  const add = async () => {
    if (!pick || busy) return;
    setBusy(true);
    try {
      const r = await api.addGroupThreadParty(thread.id, pick);
      setSlug('');
      // added=false 是幂等重放（别人刚拉过同一个组），不是错误——但也不该报"已加入"
      // 那句成功话术，否则两种不同的事实读起来一模一样。
      onAdded(r?.added !== false);
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5, alignItems: { sm: 'center' } }}>
      <FormControl size="small" sx={{ minWidth: 220 }} disabled={busy}>
        <InputLabel id="invite-party-label">{t('threads.inviteLabel')}</InputLabel>
        <Select
          labelId="invite-party-label"
          label={t('threads.inviteLabel')}
          value={pick}
          onChange={(e) => setSlug(e.target.value)}
        >
          {list.map((g) => (
            <MenuItem key={g.slug} value={g.slug}>{threadGroupLabel(g, knownGroups, lang)}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button size="small" variant="outlined" onClick={add} disabled={!pick || busy}>
        {t('threads.invite')}
      </Button>
      <Typography variant="caption" color="text.secondary">{t('threads.inviteHint')}</Typography>
    </Stack>
  );
}

// ── 归档动作条（第一百二十五批 C）─────────────────────────────────────────────
// 三个按钮，**同一时刻只可能出现一个**——显隐判据整条在 threads.mjs 的三只谓词里
// （`.jsx` 不进单测，SB-042；判定写在这里就没有任何判据看得住它，第一百零八批 R1
// 为同一件事付过账）。这里只问结论、只负责发请求与说人话。
// 提议归档要**二次确认**：它启动一个三天后自己完成的动作，而误点的代价不是"再点
// 一次撤销"，是别人会收到一条要他回应的通知。取消与重开不确认——两者都是把状态
// 拉回更保守的那一侧，误点无害。
function ArchiveActions({ thread, myGroups, canLiaison, onDone, onError }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const canRequest = canRequestArchive(thread, myGroups, canLiaison);
  const canCancel = canCancelArchive(thread, myGroups, canLiaison);
  const canReopen = canReopenThread(thread, myGroups, canLiaison);
  if (!canRequest && !canCancel && !canReopen) return null;

  // 三参而不是两参（第一百二十六批修）：错误键**由调用点带出**。此前三个动作共用
  // 一句 threads.archive.requestError（"提议归档失败。"）——取消归档失败时它是一句
  // 错的话，而两侧字典里明明各躺着 cancelError / reopenError，全仓零引用。
  // 成功回执把服务端那一份 r 一并带出去：`threads.archive.requested` 要说的是
  // **到期时刻**，而时刻只有服务端算得出（窗口长度是它的配置）。
  const run = async (fn, okKey, errKey) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fn(thread.id);
      setConfirming(false);
      onDone(okKey, r);
    } catch (e) {
      onError(e, errKey);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
      {canRequest ? (
        <Button size="small" variant="outlined" disabled={busy}
                onClick={() => setConfirming(true)}>
          {t('threads.archive.request')}
        </Button>
      ) : null}
      {canCancel ? (
        <>
          <Button size="small" variant="contained" disabled={busy}
                  onClick={() => run(api.cancelGroupThreadArchive, 'threads.archive.cancelled',
                                     'threads.archive.cancelError')}>
            {t('threads.archive.cancel')}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t('threads.archive.pendingHint')}
          </Typography>
        </>
      ) : null}
      {canReopen ? (
        <>
          <Button size="small" variant="contained" disabled={busy}
                  onClick={() => run(api.reopenGroupThread, 'threads.archive.reopened',
                                     'threads.archive.reopenError')}>
            {t('threads.archive.reopen')}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t('threads.archive.archivedHint')}
          </Typography>
        </>
      ) : null}
      <Dialog open={confirming} onClose={busy ? undefined : () => setConfirming(false)}
              fullWidth maxWidth="xs">
        <DialogTitle>{t('threads.archive.request')}</DialogTitle>
        <DialogContent>
          {/* 确认框里说的是**沉默的后果**，不是"你确定吗"：唯一值得确认的信息是
              "到期它自己就成了，而且别人要为此出声"。
              用 confirmBody 而**不是** requested（第一百二十六批修）：后者是完成时
              （「已提议归档…」）——人还没点，界面已经在说事情成了。确认框只许说
              将来时，否则它教会用户"这个框里的话不用当真"。 */}
          <Typography variant="body2">{t('threads.archive.confirmBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)} disabled={busy}>{t('common.cancel')}</Button>
          <Button variant="contained" disabled={busy}
                  onClick={() => run(api.requestGroupThreadArchive, 'threads.archive.requested',
                                     'threads.archive.requestError')}>
            {t('threads.archive.request')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ── 回帖框（canLiaison 才渲染）────────────────────────────────────────────────
// 独立组件、由调用侧以 key={thread.id} 挂载：换线即整件重置，草稿与代表组不会
// 跟着人跳到另一条线上——这比在父组件里用 effect 清 state 干净（reports.mjs
// 头注 2 那笔学费的另一种花法）。
function ReplyComposer({ thread, myGroups, onSent, onError }) {
  const t = useT();
  const { lang } = useLang();
  const [body, setBody] = useState('');
  const [asRaw, setAsRaw] = useState('');
  const [busy, setBusy] = useState(false);

  const dflt = defaultAsGroup(thread, myGroups);
  // 生效值做派生：raw 不在**合法选项**（参与集 ∩ 我的组）里就落回默认——
  // 选择器里永远不会出现一个后端注定 403 的值（对抗审查坐实首版拿两端全集当选项）。
  const asGroup = dflt.options.some((g) => g.slug === asRaw) ? asRaw : dflt.slug;
  const canSend = Boolean(body.trim()) && Boolean(asGroup) && !busy;

  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    try {
      // 201 的报文里带着动作**之后**的 archiveState（后端特意给的，供对账）——
      // 原样交给调用侧：待归档的线上回一句话会自动取消那次归档（裁决④），
      // 而此前这件事在界面上零提示（那枚「待归档」徽标只是悄悄不见了）。
      const r = await api.replyGroupThread(thread.id, { body: body.trim(), asGroup });
      setBody('');
      onSent(r);
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  // 有 liaison 权但不属于任何参与组：没有合法的代表组，整个写面不渲染——
  // 只给一句说明，而不是一个每次点发送都 403 的表单。
  if (dflt.options.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ mb: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {t('threads.notAParty')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1 }}>
        {/* 代表组选项 = 参与集 ∩ 我的组（defaultAsGroup 是唯一来源）。
            恰一个合法组时锁定：没有第二个合法选择时给一个可切的选择器
            只会诱导误选，disabled 的选择器仍然把"以谁的名义"显示出来。 */}
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={dflt.locked || busy}>
          <InputLabel id="reply-as-label">{t('threads.replyAs')}</InputLabel>
          <Select
            labelId="reply-as-label"
            label={t('threads.replyAs')}
            value={asGroup}
            onChange={(e) => setAsRaw(e.target.value)}
          >
            {dflt.options.map((g) => (
              <MenuItem key={g.slug} value={g.slug}>{threadGroupLabel(g, myGroups, lang)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <TextField
        fullWidth
        multiline
        minRows={3}
        size="small"
        label={t('threads.replyBody')}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        disabled={busy}
        sx={{ mb: 1 }}
      />
      <Button variant="contained" size="small" onClick={send} disabled={!canSend}>
        {t('threads.reply')}
      </Button>
    </Box>
  );
}

// ── 发起新线（canLiaison 才渲染入口）─────────────────────────────────────────
function NewThreadDialog({ open, onClose, options, fromOptions, knownGroups, defaultFrom, onCreated, onError }) {
  const t = useT();
  const { lang } = useLang();
  const [subject, setSubject] = useState('');
  const [fromRaw, setFromRaw] = useState('');
  const [toGroups, setToGroups] = useState([]);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  // FROM 只列我的组（fromGroupOptions；非成员组选中必 403，见 threads.mjs 注）；
  // TO 保持并集（目的地可以是任何在系统里露过面的组）且**多选**（第一百零六批 C1：
  // 三方对表不该被逼成两条线），但滤掉当前 FROM——发起组混进对象里是误选。
  const fromSlugs = fromOptions.map((g) => g.slug);
  // 发起组默认当前组（用户此刻站在哪个组，多半就替它说话）；raw 不合法即回落。
  const fromGroup = fromSlugs.includes(fromRaw) ? fromRaw
    : (fromSlugs.includes(defaultFrom) ? defaultFrom : (fromSlugs[0] ?? ''));
  const toOptions = options.filter((g) => g.slug !== fromGroup);
  // 生效的多选值做**派生**：换了发起组之后，原先选中的那个组可能已经成了发起组，
  // 留在对象里就是一个注定被后端折叠掉的选项（用户看得见、却不会生效）。
  const toSlugs = toGroups.filter((s) => toOptions.some((g) => g.slug === s));
  const problem = newThreadProblem({ subject, fromGroup, toGroups: toSlugs, body });

  const create = async () => {
    if (problem || busy) return;
    setBusy(true);
    try {
      const r = await api.createGroupThread({
        subject: subject.trim(),
        // 发起组恒在第 0 位（后端拿它署开场白并判成员闸），去重保序在 mjs 里。
        parties: newThreadParties(fromGroup, toSlugs),
        body: body.trim(),
      });
      setSubject(''); setFromRaw(''); setToGroups([]); setBody('');
      // 形状以实际返回为准（合同注）：包裹/平铺两收，都没有就只刷列表不选中。
      onCreated(r?.thread?.id ?? r?.id ?? null);
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  const groupSelect = (labelKey, id, value, onChange, list) => (
    <FormControl size="small" fullWidth disabled={busy}>
      <InputLabel id={id}>{t(labelKey)}</InputLabel>
      <Select labelId={id} label={t(labelKey)} value={value} onChange={onChange}>
        {list.map((g) => (
          <MenuItem key={g.slug} value={g.slug}>{threadGroupLabel(g, knownGroups, lang)}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('threads.new')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            size="small"
            fullWidth
            label={t('threads.form.subject')}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={busy}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {groupSelect('threads.form.from', 'thread-from-label', fromGroup,
              (e) => setFromRaw(e.target.value), fromOptions)}
            {/* 对象组**多选**（第一百零六批 C1）：一次三方对表就是一条三方线，
                不该被逼成两条线靠人工交叉引用。选中项渲染成徽标而不是逗号串——
                徽标与线上那排参与组徽标同形，用户一眼看出"这条线会长成什么样"。 */}
            <FormControl size="small" fullWidth disabled={busy}>
              <InputLabel id="thread-to-label">{t('threads.form.to')}</InputLabel>
              <Select
                labelId="thread-to-label"
                label={t('threads.form.to')}
                multiple
                value={toSlugs}
                onChange={(e) => setToGroups(
                  typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(picked) => (
                  <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {picked.map((s) => (
                      <Chip key={s} size="small" variant="outlined" color="info"
                            label={threadGroupLabel(s, knownGroups, lang)} />
                    ))}
                  </Stack>
                )}
              >
                {toOptions.map((g) => (
                  <MenuItem key={g.slug} value={g.slug}>
                    {threadGroupLabel(g, knownGroups, lang)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={4}
            label={t('threads.form.body')}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={busy}
          />
          {/* 灰按钮必须说得出为什么灰（第八十五批建组表单的口径）：
              缺哪条 newThreadProblem 就说哪条，齐了这行整个消失。 */}
          {problem ? (
            <Typography variant="caption" color="text.secondary">{t(problem)}</Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={create} disabled={Boolean(problem) || busy}>
          {t('threads.form.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 看板本体。**只有过了 C1 那道闸的人才会挂载它**——本页所有的 hook（含两次取数）
// 都长在这里，于是外层那个闸可以是一句干净的早退，而不必在 hook 之间穿插条件。
function ThreadsBoard() {
  const t = useT();
  const { lang } = useLang();
  const { account, groups, currentGroup } = useAuth();
  const canLiaison = Boolean(account?.canLiaison);
  const { snack, notify, clearSnack } = useNotify();
  const location = useLocation();

  // 攥在手里的只是"用户点过哪条"，生效的 id 由 pickThreadId 派生（reports.mjs
  // 头注 2 的形状：列表重载后旧 id 可能不存在，派生值不可能滞后）。
  const [wantedId, setWantedId] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  // 归档筛选（第一百二十五批 C，裁决③：默认列表隐藏、可切换显示）。
  // **本页的第一个筛选器**，所以口径写在这里：过滤在**服务端**做（?includeArchived=1），
  // 前端只切开关并重取——把全量端下来再在前端滤掉，等于让"默认隐藏"这条裁决在
  // 三个调用方各实现一遍。
  const [showArchived, setShowArchived] = useState(false);

  // ── 地址栏的 focus / showArchived：**每次导航**都重读，不是挂载初值 ──────────
  // 病灶（第一百二十六批实证）：这两位原本是 `useState(() => …fromSearch(location.search))`
  // ——惰性初值**只在首次渲染求值**。而 `/threads?focus=3` → `/threads?focus=9&showArchived=1`
  // 是同一条路由、同一个挂载实例（下面挂 <ThreadsBoard/> 时无 key），于是第二次导航
  // 什么都不会发生。这恰好击中清欠链逐条 navigate 的形状：两条 group_thread 通知连着
  // 来时，第二条落在第一条选中的线上；`thread_archived` 那条的 showArchived=1 也不生效
  // ⇒ 跳到一个**看不见那条线**的页面，而那看起来像"这条线没了"。
  // 对照组：MessagesPage 的 focusId 是每次渲染现算的（做法对，只是这页攥着选中态）。
  //
  // 依赖 `location.key`：它每次导航都变（同一 URL 再跳一次也变），而 location.search
  // 在"同一条链被点两次"时是相等的——用 search 作依赖会漏掉那一次。
  // **只加不减**：URL 说得出 focus 就选中它、说 showArchived 就把开关打开；URL 里
  // 什么都没有时**不动**用户此刻的选择——地址栏是一条"带我去看这个"的指令，
  // 不是一份关于用户偏好的完整声明（把开关按 URL 关回去，等于每跳一次就把他刚打开的
  // 归档柜关上）。
  useEffect(() => {
    const focus = focusIdFromSearch(location.search);
    if (focus) setWantedId(focus);
    if (showArchivedFromSearch(location.search)) setShowArchived(true);
    // 只认导航这一个触发。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // 不带任何组参数——透明口径，见头注。
  const list = useLoad(
    async () => ({ threads: normalizeThreads(await api.groupThreads({ includeArchived: showArchived })) }),
    [showArchived]);
  const threads = list.data?.threads ?? [];
  const selectedId = pickThreadId(threads, wantedId);

  const detail = useLoad(async () => {
    if (!selectedId) return null;
    return normalizeThreadDetail(await api.groupThread(selectedId));
  }, [selectedId]);

  const thread = detail.data?.thread ?? null;
  const messages = detail.data?.messages ?? [];

  // 写面的两种"有话直说"的拒绝：403 = 缺 group_liaison 权位（写面显隐只看
  // canLiaison，权位被收走而会话没刷新时才会撞上）；THREAD_CLOSED(409) = 正打字
  // 时线被关了（关闭后回帖框不渲染，见下——这条只兜竞态）。
  // 归档那四种 409 各说各的下一步（后端逐个分了码）：把它们压成一句"再试一次"
  // 会让其中三种变成死路——被否决过的线再试一万次都是 409。
  const writeErr = (fallback) => (e) =>
    notify(t(errKey(e, {
      byCode: {
        THREAD_CLOSED: 'threads.closedError',
        THREAD_ARCHIVED: 'threads.archive.archivedError',
        ARCHIVE_PENDING: 'threads.archive.pendingError',
        ARCHIVE_VETOED: 'threads.archive.vetoedError',
        NO_ARCHIVE_PENDING: 'threads.archive.nonePendingError',
        NOT_ARCHIVED: 'threads.archive.notArchivedError',
      },
      byStatus: { 403: 'threads.forbidden' },
      fallback,
    })), 'error');

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 0.5 }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
          {t('threads.title')}
          <HelpLink section="threads" />
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          {/* 本页的第一个筛选器（第一百二十五批 C）。**读面的开关，不受 canLiaison
              限制**：归档只是收纳，看不看得见归档柜与能不能动它是两回事。 */}
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
            )}
            label={<Typography variant="body2">{t('threads.archive.showArchived')}</Typography>}
          />
          {/* 写面显隐的唯一依据：canLiaison（读面全员可见）。 */}
          {canLiaison ? (
            <Button variant="contained" size="small" onClick={() => setNewOpen(true)}>
              {t('threads.new')}
            </Button>
          ) : null}
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('threads.subtitle')}
      </Typography>

      {/* ── 空态三分：加载中 / 加载失败 / 还没有线 ─────────────────────────
          与 Reports 的四分少一格：本页不按当前组过滤（透明口径），"没有当前组"
          在这里不是一个存在的状态——那一格由页头那句说明抵掉。 */}
      {list.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : list.status === 'error' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>
            {t(errKey(list.error, { fallback: 'threads.error' }))}
          </Alert>
          <Button size="small" onClick={list.reload}>{t('common.retry')}</Button>
        </Box>
      ) : threads.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('threads.empty')}</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          {/* ── 左：线列表（高度上限的理由同 ReportsPage 的清单）─────────── */}
          <Paper
            variant="outlined"
            sx={{
              width: { xs: '100%', md: 340 },
              flexShrink: 0,
              maxHeight: { xs: '40vh', md: 'calc(100vh - 220px)' },
              overflow: 'auto',
              alignSelf: { md: 'flex-start' },
            }}
          >
            <List disablePadding>
              {threads.map((th) => (
                <ListItemButton
                  key={th.id}
                  selected={th.id === selectedId}
                  onClick={() => setWantedId(th.id)}
                  sx={{ display: 'block', py: 1 }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{ alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
                      {th.subject}
                    </Typography>
                    {statusChip(t, th.status)}
                    {archiveChip(t, th, lang)}
                    {th.viaAgent ? <AgentChip /> : null}
                  </Stack>
                  <Box sx={{ mt: 0.5 }}>
                    <PartyChips thread={th} knownGroups={groups} lang={lang} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {t('threads.messages', { n: th.messageCount })}
                    {th.lastAtMs ? ` · ${fmtTime(th.lastAtMs, lang)}` : ''}
                  </Typography>
                </ListItemButton>
              ))}
            </List>
          </Paper>

          {/* ── 右：消息流 ──────────────────────────────────────────────── */}
          <Paper variant="outlined" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, sm: 3 } }}>
            {detail.status === 'loading' ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : detail.status === 'error' ? (
              <Box>
                <Alert severity="error" sx={{ mb: 1 }}>
                  {t(errKey(detail.error, { byStatus: { 404: 'threads.gone' },
                                            fallback: 'threads.detailError' }))}
                </Alert>
                <Button size="small" onClick={detail.reload}>{t('common.retry')}</Button>
              </Box>
            ) : !thread ? (
              <Typography color="text.secondary">{t('threads.selectHint')}</Typography>
            ) : (
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 2 }}
                >
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 700, minWidth: 0, overflowWrap: 'anywhere' }}>
                    {thread.subject}
                  </Typography>
                  {statusChip(t, thread.status)}
                  {archiveChip(t, thread, lang)}
                  {thread.viaAgent ? <AgentChip /> : null}
                  <PartyChips thread={thread} knownGroups={groups} lang={lang} />
                </Stack>

                {/* 归档三件（第一百二十五批 C）。显隐判据整条在 threads.mjs 的三只
                    谓词里；这里只挂件。key={thread.id}：换线即重置确认框。 */}
                <ArchiveActions
                  key={`arch-${thread.id}`}
                  thread={thread}
                  myGroups={groups}
                  canLiaison={canLiaison}
                  onDone={(okKey, r) => {
                    // 「已提议归档」那一句要说出**到期时刻**（服务端算的，窗口长度是
                    // 它的配置——写死一个"三日"在这里，配置改成 7 的当天它就是假话）。
                    // 窗口被关掉时换哪一句由 archiveDoneKey 判（.jsx 不进单测，SB-042）。
                    notify(t(archiveDoneKey(okKey, r?.archiveDueAtMs),
                             { at: fmtTime(Number(r?.archiveDueAtMs), lang) }), 'success');
                    detail.reload();
                    list.reload();   // 归档徽标也长在列表行上，且归档会把行滤出去
                  }}
                  onError={(e, errKey) => writeErr(errKey)(e)}
                />

                {/* 追加参与方（第一百零六批 C1）：canLiaison ∧ 我已在场 ∧ 线未关闭。
                    判据整条在 canInviteParty 里，这里只问结论——显隐逻辑写在 JSX 里
                    就没有任何判据看得住它。key={thread.id}：换线即重置选中的组。 */}
                {canInviteParty(thread, groups, canLiaison) ? (
                  <InviteParty
                    key={thread.id}
                    thread={thread}
                    options={groupOptionsForNewThread(threads, groups)}
                    knownGroups={groups}
                    lang={lang}
                    onAdded={(added) => {
                      notify(t(added ? 'threads.invited' : 'threads.inviteNoop'),
                             added ? 'success' : 'info');
                      detail.reload();
                      list.reload();   // 参与组徽标也长在列表行上，一起刷
                    }}
                    onError={writeErr('threads.inviteError')}
                  />
                ) : null}

                {messages.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('threads.noMessages')}
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {messages.map((m) => (
                      <Box key={`${m.seq}-${m.atMs}`}>
                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}
                        >
                          {/* seq 是消息在这条线里的地面真值（asc 的排序键），
                              印出来问题才有报的坐标——同 reports 页印 reportKey。 */}
                          <Typography variant="caption" color="text.secondary"
                                      sx={{ fontFamily: 'ui-monospace, monospace' }}>
                            {`#${m.seq}`}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {m.authorDisplay || '—'}
                          </Typography>
                          {/* 代表组徽标：消息的主体是**组**不是人，这枚徽标就是
                              那句话的显示落点。会话上下文认识这个 slug 就显双语名，
                              不认识（别组的线）就如实印 slug——绝不静默省略。 */}
                          <Chip
                            size="small"
                            variant="outlined"
                            color="info"
                            label={threadGroupLabel(m.asGroup, groups, lang) || '—'}
                            title={t('threads.asGroupHint')}
                          />
                          {m.viaAgent ? <AgentChip /> : null}
                          <Typography variant="caption" color="text.secondary">
                            {m.atMs ? fmtTime(m.atMs, lang) : '—'}
                          </Typography>
                        </Stack>
                        <MarkdownBody body={m.body} format="markdown" />
                      </Box>
                    ))}
                  </Stack>
                )}

                {/* 写面：canLiaison=false ⇒ 整件不存在（不是禁用，见头注）。
                    closed 线同样不渲染回帖框：后端对 closed 一律 409（THREAD_CLOSED），
                    在一条已关闭的线上摆一个回帖框就是在撒谎。status 空串（形状漂移
                    的兜底取值）按 open 待之——真闸在后端，这里只是不说谎。
                    第一百二十五批 C：判定整条搬进 canReplyToThread（.jsx 不进单测，
                    SB-042）。**待归档照常给回帖框**——那正是裁决④的否决路：
                    在线里说一句话就自动取消了归档，藏起它等于把这条路堵死。 */}
                {canReplyToThread(thread, canLiaison) ? (
                  <ReplyComposer
                    key={thread.id}
                    thread={thread}
                    myGroups={groups}
                    onSent={(r) => {
                      // 裁决④：待归档的线上发一句话＝自动取消那次归档。判定在
                      // replyCancelledArchive（.jsx 不进单测，SB-042），这里只问结论。
                      // 不说出来的话，用户既不知道自己撤了别人的提议，也无从判断
                      // 那枚忽然消失的「待归档」徽标是不是个 bug。
                      notify(t(replyCancelledArchive(thread, r)
                        ? 'threads.repliedArchiveCancelled' : 'threads.replied'), 'success');
                      detail.reload();
                      list.reload();   // 消息数与最新时间在列表行上，也要跟着变
                    }}
                    onError={writeErr('threads.replyError')}
                  />
                ) : null}
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {canLiaison ? (
        <NewThreadDialog
          open={newOpen}
          onClose={() => setNewOpen(false)}
          options={groupOptionsForNewThread(threads, groups)}
          fromOptions={fromGroupOptions(groups)}
          knownGroups={groups}
          defaultFrom={currentGroup?.slug ?? ''}
          onCreated={(id) => {
            setNewOpen(false);
            notify(t('threads.created'), 'success');
            if (id != null) setWantedId(String(id));
            list.reload();
          }}
          onError={writeErr('threads.createError')}
        />
      ) : null}

      <AppSnackbar snack={snack} onClose={clearSnack} />
    </Box>
  );
}

// ── C1 闸（第一百零八批）──────────────────────────────────────────────────────
// 非执行层（external / principal）到这里就停：不取数、不渲染看板，只给一句说得出
// 理由的拒绝。三个刻意选择：
//   · **拒绝而不是空页**——空页与"系统坏了"无法区分，而这是一个有答案的状态；
//   · **severity="info" 而不是 error**——这不是错误，是这个面本就不面向他，
//     红色警告会让人去开工单问"为什么坏了"；
//   · 页头标题照留：他从导航或旧书签过来时，得先认出"这是哪一页"才读得懂那句话。
// 导航入口那一侧同样按 canSeeThreadsPage 隐藏（appNav.mjs），两处共用一个谓词；
// 这一层是给书签、深链、以及入口刚被收走那半拍准备的。真边界在后端（整族 404）。
//
// ── 第一百零八批 R1（D3）：判定不在这个文件里 ────────────────────────────────
// 这里**只问结论**，一个字的判据都不写。首版把 `account?.party === 'internal'` 写在
// 这一行，而 `.jsx` 不进单测（SB-042）——于是把整句早退删掉，前端全套仍然全绿：
// 一个不该看见这页的人照常挂载看板、打两次 404、看到"加载失败"。判定现在住在
// appNav.mjs 的 canSeeThreadsPage 里（那里有变异会红的用例），而"这句守卫还在、
// 且排在挂载 <ThreadsBoard/> 之前"由 appNav.test.mjs 直接读本文件源码钉死。
export default function ThreadsPage() {
  const t = useT();
  const { account } = useAuth();
  if (!canSeeThreadsPage(account)) {
    return (
      <Box sx={{ maxWidth: 720 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 1.5 }}>
          {t('threads.title')}
        </Typography>
        <Alert severity="info">{t('threads.noAccess')}</Alert>
      </Box>
    );
  }
  return <ThreadsBoard />;
}
